import {
	DateRangePicker,
	ExportRequestModal,
	ExportSuccessModal,
	FormFieldControl,
	MultiSelect,
} from "@components";
import { SearchLg } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { DateValue, Selection } from "react-aria-components";
import type { SelectItemType } from "@/shared/components/select/select-shared";
import { useExportExpenses } from "@/shared/hooks";
import { REPORT_TYPE } from "@/shared/types/generate-report-request";
import {
	buildGenerateReportRequest,
	formatDateValueToApi,
	normalizeExportFilterIds,
	selectionToNumberIds,
	validateExportDateRange,
} from "@/shared/utils";
import { REPORT_SUMMARY_CATEGORY_NAMES } from "../constants";
import { useGetVehiclesLookup } from "../hooks";
import type { ExpenseTypeCode } from "../types";

type DateRangeValue = {
	start: DateValue;
	end: DateValue;
} | null;

type ExpensesExportModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function ExpensesExportModal({
	isOpen,
	onClose,
}: ExpensesExportModalProps) {
	const [selectedTypeIds, setSelectedTypeIds] = useState<ExpenseTypeCode[]>([]);
	const [selectedVehicleIds, setSelectedVehicleIds] = useState<number[]>([]);
	const [dateRange, setDateRange] = useState<DateRangeValue>(null);
	const [pullAllSinceStart, setPullAllSinceStart] = useState(false);
	const [typesError, setTypesError] = useState<string | null>(null);
	const [dateError, setDateError] = useState<string | null>(null);
	const [isSuccessOpen, setIsSuccessOpen] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const { data: vehiclesData = [] } = useGetVehiclesLookup(isOpen);

	const expenseTypeItems = useMemo<SelectItemType[]>(
		() =>
			(Object.entries(REPORT_SUMMARY_CATEGORY_NAMES) as [string, string][]).map(
				([key, label]) => ({
					id: String(key),
					label,
				})
			),
		[]
	);

	const vehicleItems = useMemo<SelectItemType[]>(
		() =>
			vehiclesData.map((vehicle) => ({
				id: String(vehicle.value),
				label: vehicle.label,
			})),
		[vehiclesData]
	);

	const allTypeIds = useMemo(
		() => expenseTypeItems.map((item) => Number(item.id)),
		[expenseTypeItems]
	);

	const allVehicleIds = useMemo(
		() => vehicleItems.map((item) => Number(item.id)),
		[vehicleItems]
	);

	const selectedTypeKeys = useMemo(
		() => new Set<string>(selectedTypeIds.map(String)),
		[selectedTypeIds]
	);

	const selectedVehicleKeys = useMemo(
		() => new Set<string>(selectedVehicleIds.map(String)),
		[selectedVehicleIds]
	);

	const exportMutation = useExportExpenses({
		onSuccess: (response) => {
			onClose();
			setSuccessMessage(response.message);
			setIsSuccessOpen(true);
		},
	});

	useEffect(() => {
		if (!isOpen) {
			setSelectedTypeIds([]);
			setSelectedVehicleIds([]);
			setDateRange(null);
			setPullAllSinceStart(false);
			setTypesError(null);
			setDateError(null);
		}
	}, [isOpen]);

	const handleTypesSelectionChange = (keys: Selection) => {
		setSelectedTypeIds(
			selectionToNumberIds(keys, allTypeIds) as ExpenseTypeCode[]
		);
		setTypesError(null);
	};

	const handleVehiclesSelectionChange = (keys: Selection) => {
		setSelectedVehicleIds(selectionToNumberIds(keys, allVehicleIds));
	};

	const handleSubmit = () => {
		let hasError = false;

		const validationError = validateExportDateRange(
			dateRange?.start,
			dateRange?.end,
			{ pullAllSinceStart, isRequired: false }
		);
		if (validationError) {
			setDateError(validationError);
			hasError = true;
		} else {
			setDateError(null);
		}

		if (hasError) {
			return;
		}

		exportMutation.mutate(
			buildGenerateReportRequest({
				type: REPORT_TYPE.Expenses,
				initialDate: formatDateValueToApi(dateRange?.start),
				finalDate: formatDateValueToApi(dateRange?.end),
				pullAllSinceStart,
				expenseTypeIds: normalizeExportFilterIds(selectedTypeIds, allTypeIds),
				vehicleIds: normalizeExportFilterIds(selectedVehicleIds, allVehicleIds),
			})
		);
	};

	return (
		<>
			<ExportRequestModal
				description="Digite o tipo de despesa e a data"
				isOpen={isOpen}
				isSubmitting={exportMutation.isPending}
				onClose={onClose}
				onSubmit={handleSubmit}
				title="Exportar Despesas"
			>
				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={typesError ?? undefined}
					htmlFor="expenses-export-type"
					label="Despesa"
				>
					<MultiSelect
						aria-label="Digite ou selecione o(s) nome(s) da(s) despesa(s)"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum despesa encontrado"
						icon={SearchLg}
						isInvalid={!!typesError}
						items={expenseTypeItems}
						onSelectionChange={handleTypesSelectionChange}
						placeholder="Digite ou selecione o(s) nome(s) da(s) despesa(s)"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 tipo" : `${count} tipos`
						}
						selectedKeys={selectedTypeKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					htmlFor="expenses-export-vehicles"
					label="Veículo"
				>
					<MultiSelect
						aria-label="Selecionar veículos"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum veículo encontrado"
						icon={SearchLg}
						items={vehicleItems}
						onSelectionChange={handleVehiclesSelectionChange}
						placeholder="Digite ou selecione o(s) veículo(s)"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 veículo" : `${count} veículos`
						}
						selectedKeys={selectedVehicleKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[70px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={dateError ?? undefined}
					htmlFor="expenses-export-date"
					label="Data"
				>
					<DateRangePicker
						aria-label="Período de exportação de despesas"
						buttonClassName="w-full justify-start px-4"
						className="w-full"
						onChange={(range) => {
							setDateRange(range);
							setDateError(null);
						}}
						onPullAllSinceStartChange={(value) => {
							setPullAllSinceStart(value);
							if (value) {
								setDateRange(null);
							}
							setDateError(null);
						}}
						pullAllSinceStart={pullAllSinceStart}
						showPullAllSinceStart
						value={dateRange}
					/>
				</FormFieldControl>
			</ExportRequestModal>

			<ExportSuccessModal
				isOpen={isSuccessOpen}
				message={successMessage}
				onClose={() => setIsSuccessOpen(false)}
			/>
		</>
	);
}
